import {
  CUSTOM_FIELD_TYPES,
  useDeleteCustomEmployeeFieldMutation,
  useGetCustomEmployeeFieldsQuery,
  type CustomEmployeeField,
} from '@/app/features/control-panel/organization';
import ConfirmationModal from '@/components/ConfirmationModal';
import CustomTable from '@/components/CustomTable';
import StatusTag from '@/components/StatusTag';
import { DISCLOSER_KEYS, FILTER_KEYS } from '@/constants/keys.constants';
import { useAppDiscloser, useAppFilters } from '@/hooks';
import { PencilEdit02Icon, TrashIcon } from '@/icons';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { Button, Space, Tag, type TableProps } from 'antd';
import ManageCustomField from './_components/CustomFields/ManageCustomField';

const STATUS_CONFIG = [
  { label: 'Active', value: 'active', className: 'status status-success' },
  { label: 'Inactive', value: 'inactive', className: 'status status-error' },
];

const labelOf = (options: { label: string; value: string }[], value: string) =>
  options.find((option) => option.value === value)?.label || value;

/**
 * Company-defined extra employee fields (JTI step 7).
 *
 * A company lists the fields it needs here; the employee create form, the edit
 * page and the details screen render them together under "Additional
 * Information", separate from the built-in sections. A company that adds
 * nothing sees every employee screen exactly as it is today.
 */
const CustomFields = () => {
  const [config, handleOpen, handleClose] = useAppDiscloser(
    DISCLOSER_KEYS.ORG_CUSTOM_FIELDS,
  );
  const { page, limit, keyword, filters, handlePagination } = useAppFilters(
    FILTER_KEYS.ORG_CUSTOM_FIELDS,
  );

  const { data, isLoading, isFetching } = useGetCustomEmployeeFieldsQuery({
    params: { page, limit, search: keyword, ...filters },
  });

  const [deleteField, { isLoading: isDeleting }] =
    useDeleteCustomEmployeeFieldMutation();

  const handleDelete = async () => {
    try {
      await deleteField({ id: config?.data?.id }).unwrap();
      toast('success', 'Field removed successfully');
      handleClose();
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to remove the field'));
    }
  };

  const columns: TableProps<CustomEmployeeField>['columns'] = [
    {
      title: 'Field Name',
      dataIndex: 'field_name',
      render: (value: string) => value || 'n/a',
    },
    {
      title: 'Type',
      dataIndex: 'field_type',
      render: (value: string, record) =>
        value === 'SELECT' ? (
          <Space size={4} wrap>
            <span>{labelOf(CUSTOM_FIELD_TYPES, value)}</span>
            <span className="text-white/40 text-xs">
              ({record.options?.length || 0} options)
            </span>
          </Space>
        ) : (
          labelOf(CUSTOM_FIELD_TYPES, value)
        ),
    },
    {
      title: 'Required',
      dataIndex: 'is_required',
      align: 'center',
      render: (value: boolean) =>
        value ? (
          <Tag className="!bg-transparent !border-theme-primary !text-theme-primary !rounded">
            Required
          </Tag>
        ) : (
          <span className="text-white/40">Optional</span>
        ),
    },
    {
      title: 'Order',
      dataIndex: 'sort_order',
      align: 'center',
      render: (value: number) => value ?? 'n/a',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      render: (status: string) => (
        <StatusTag value={status} config={STATUS_CONFIG} />
      ),
    },
    {
      title: 'Actions',
      align: 'end',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            className="action-btn"
            icon={<PencilEdit02Icon size={20} />}
            onClick={() => handleOpen({ type: 'UPDATE', data: record })}
          />
          <Button
            size="small"
            className="action-btn"
            icon={<TrashIcon size={20} />}
            onClick={() => handleOpen({ type: 'DELETE', data: record })}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <CustomTable
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        loading={isLoading || isFetching}
        onPaginate={handlePagination}
        tableRootClassName="border-0 p-0"
        dataSource={data?.data?.data || []}
        total={data?.data?.meta?.total || 0}
      />

      <ManageCustomField
        open={config.open && config.type !== 'DELETE'}
        field={config.type === 'UPDATE' ? config.data : undefined}
        handleClose={handleClose}
      />

      <ConfirmationModal
        open={config.open && config.type === 'DELETE'}
        handleClose={handleClose}
        handleSubmit={handleDelete}
        loading={isDeleting}
        title="Remove Custom Field"
        subTitle={`Remove "${config?.data?.field_name}" from the employee forms? Values already saved for employees are kept.`}
        buttonName="Remove"
      />
    </div>
  );
};

export default CustomFields;
